import { Moment } from "moment";

import { REPORT_SENSOR_REGISTRY } from "constant/report";
import {
    ReportTimeRangeType,
    ReportItemSummaryType,
    ReportItemType,
    ReportItemsState,
    NullableNumber,
} from "types/Reports";
import { formatDateInReport } from "./reportFormat";

export const getPageNumberInPDF = ({ pageNumber, totalPages }: { pageNumber: number; totalPages: number }) => {
    // totalPages can be undefined in the middle processing (not at the end of the processing)
    return `${(pageNumber || 1).toString().padStart(2, "0")} / ${(totalPages || pageNumber || 1).toString().padStart(2, "0")}`;
};

export const getReportPeriodTypeString = (type: ReportTimeRangeType) => {
    switch (type) {
        case 'weekdays':
            return 'Weekdays Only';
        case 'weekends':
            return 'Weekends Only';
        default:
            return 'All Days';
    }
}

export const getTitleByTime = (timeRangeType: ReportTimeRangeType) => {
    let titleTimeText = '';

    if (timeRangeType === 'weekdays') {
        titleTimeText = ' (Weekdays Only)';
    } else if (timeRangeType === 'weekends') {
        titleTimeText = ' (Weekends Only)';
    }

    return titleTimeText;
}

export const getTimeTextForHighestLowest = (timeRangeType: ReportTimeRangeType) => {
    let timeText = '';

    if (timeRangeType === 'weekdays') {
        timeText = 'On weekdays, ';
    } else if (timeRangeType === 'weekends') {
        timeText = 'On weekends, ';
    }

    return timeText;
};

export const getHighestOrLowestDayDisplay = (dates: Moment[], dateFormat: string) => {
    if (dates.length < 1) {
        return '';
    }

    if (dates.length === 1) {
        return formatDateInReport(dates[0], dateFormat);
    }

    if (dates.length === 2) {
        return `${formatDateInReport(dates[0], dateFormat)}, ${formatDateInReport(dates[1], dateFormat)}`;
    };

    if (dates.length === 3) {
        return `${formatDateInReport(dates[0], dateFormat)}, ${formatDateInReport(dates[1], dateFormat)} and 1 other day`;
    }

    return `${formatDateInReport(dates[0], dateFormat)}, ${formatDateInReport(dates[1], dateFormat)} and ${dates.length - 2} other days`;
};

export const getHighestOrLowestNameDisplay = (names: string[], itemType: ReportItemSummaryType) => {
    if (names.length < 1) {
        return '';
    }

    if (names.length === 1) {
        return names[0];
    }

    if (names.length === 2) {
        return `${names[0]}, ${names[1]}`;
    }

    if (names.length === 3) {
        let typeText = '';

        switch (itemType) {
            case 'energy':
            case 'water':
                typeText = 'meter';
                break;
            case 'room':
                typeText = 'room';
                break;
            case 'waterLeakage':
                typeText = 'sensor';
                break;
        }

        return `${names[0]}, ${names[1]} and 1 other ${typeText}`;
    }

    let typeText = '';

    switch (itemType) {
        case 'energy':
        case 'water':
            typeText = 'meters';
            break;
        case 'room':
            typeText = 'rooms';
            break;
        case 'waterLeakage':
            typeText = 'sensors';
            break;
    }

    return `${names[0]}, ${names[1]} and ${names.length - 2} other ${typeText}`;
};

export const getResourceString = (items: ReportItemsState) => {
    let resources = '';

    const types = (Object.keys(items) as (keyof ReportItemsState)[]);

    for (const type of types) {
        let units = REPORT_SENSOR_REGISTRY[type].summaryName;

        const count = items[type].appliedIds.length;

        if (count > 1 && type !== 'waterLeakage') {
            units += 's';
        }

        if (count === 0) {
            continue;
        }

        if (resources.length === 0) {
            resources = `${count} ${units}`;
        } else {
            resources += `, ${count} ${units}`;
        }
    }

    return resources;
};

export const getNumberOfItemsText = (
    numberOfItems: NullableNumber,
    itemType: ReportItemSummaryType,
) => {
    let numItemText = '';
    const plural = numberOfItems !== null && numberOfItems > 1 ? 's' : '';
    switch (itemType) {
        case 'room':
            numItemText = numberOfItems === null ? '' : ` (${numberOfItems} Room${plural})`;
            break;
        case 'energy':
            numItemText = numberOfItems === null ? '' : ` (${numberOfItems} Meter${plural})`;
            break;
        case 'water':
            numItemText = numberOfItems === null ? '' : ` (${numberOfItems} Meter${plural})`;
            break;
        case 'waterLeakage':
            numItemText = numberOfItems === null ? '' : ` (${numberOfItems} Sensor${plural})`;
            break;
    }

    return numItemText;
};

// react-pdf has trouble with breaking texts for _ char and CAPITAL char
export const breakWordsForLongLine = (text: string, maxLength = 15) => {
    if (typeof text !== 'string') {
        return text;
    }

    if (text.length <= maxLength) {
        return text;
    }

    // 1. Split the text into individual words
    // Split using a regex that keeps the delimiters (using positive lookbehind)
    // This ensures "word_word" becomes ["word", "_word"] instead of losing the "_"
    const words = text.split(/(?<=[\s_-])/);
    let currentLine = '';
    const resultLines = [];

    // 2. Iterate through each word and build lines
    for (const word of words) {
        // Check if adding the next word exceeds the maxLength
        // +1 accounts for the space that will be added
        if (currentLine.length + word.length + 1 <= maxLength) {
            // Add the word to the current line (with a leading space if not the first word)
            currentLine += (currentLine.length === 0 ? '' : ' ') + word;
        } else {
            // The word doesn't fit on the current line.
            // Push the current line to the results and start a new line with the current word.
            resultLines.push(currentLine);
            currentLine = word;
        }
    }

    // 3. Push the last remaining line
    resultLines.push(currentLine);

    // 4. Join all lines with a newline character
    return resultLines.join('\n');
};

export const getIndividualCardPrefix = (type: ReportItemType) => {
    const sensorTypes: ReportItemType[] = ['temperature', 'humidity', 'co2', 'pressure', 'tvoc', 'pm1', 'pm2.5', 'pm10'];

    if (sensorTypes.includes(type)) {
        return 'Daily Average - ';
    }

    return '';
}

// Shared text fragments used by both the HTML Report cards (components/report) and the
// PDF Report cards (components/reportPDF). Each helper returns a bare fragment with no
// surrounding whitespace — callers control spacing to match their layout (JSX text
// nodes vs react-pdf <Text> elements).

export const getEdgeCountUnit = (itemType: ReportItemType, numberOfItems: NullableNumber) => {
    const entry = REPORT_SENSOR_REGISTRY[itemType];
    return numberOfItems === 1 ? entry.edgeCountSingular : entry.edgeCountPlural;
};

export const getEdgeUsedFragment = (itemType: ReportItemType) =>
    REPORT_SENSOR_REGISTRY[itemType].edgeUsedFragment;

export const getEdgeWithClause = (itemType: ReportItemType) =>
    itemType === 'waterLeakage' ? 'with a count of' : 'with';

export const getAverageSubject = (itemType: ReportItemType) =>
    REPORT_SENSOR_REGISTRY[itemType].averageSubject;

export const getPercentDirection = (percent: number) => (percent > 0 ? 'increased' : 'decreased');

export const getPercentSubText = (itemType: ReportItemType, direction: string) =>
    itemType === 'waterLeakage'
        ? `${direction} from previous month.*`
        : 'compared to the previous month.*';

export const getIndividualPeakDescriptions = (
    itemType: ReportItemSummaryType,
    inMonthDate: string,
    usageName?: string,
) => {
    const word = (usageName ?? REPORT_SENSOR_REGISTRY[itemType].individualUsageWord).toLowerCase();

    switch (itemType) {
        case 'room':
            return {
                highest: `had the highest room occupancy in ${inMonthDate} with`,
                lowest: `had the lowest room occupancy in ${inMonthDate} with`,
            };
        case 'waterLeakage':
            return {
                highest: `reported the highest ${word} this month with a count of`,
                lowest: `reported the lowest ${word} this month with a count of`,
            };
        case 'energy':
        case 'water':
        default:
            return {
                highest: `reported the highest consumed ${word} in ${inMonthDate}, with a usage of`,
                lowest: `reported the lowest consumed ${word} in ${inMonthDate}, with a usage of`,
            };
    }
};
